import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/context/LanguageContext";
import type { Rate } from "@/types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface GroupedRate {
  serviceType: string;
  rates: Rate[];
  image_url?: string;
}

const ServiceRates = () => {
  const [rates, setRates] = useState<Rate[]>([]);
  const [groupedRates, setGroupedRates] = useState<Record<string, Rate[]>>({});
  const { t } = useLanguage();

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/rates`);
      setRates(response.data);
      
      // Group rates by service type
      const grouped = response.data.reduce((acc, rate) => {
        if (!acc[rate.service_type]) {
          acc[rate.service_type] = [];
        }
        acc[rate.service_type].push(rate);
        return acc;
      }, {});
      
      setGroupedRates(grouped);
    } catch {
      // Rate fetch failed silently
    }
  };

  const sortedGroupedRates = useMemo(() => {
    return Object.entries(groupedRates).map(([serviceType, serviceRates]) => ({
      serviceType,
      rates: [...serviceRates].sort((a, b) => a.duration_minutes - b.duration_minutes),
      image_url: serviceRates[0]?.image_url
    }));
  }, [groupedRates]);

  return (
    <section className="mb-16" data-testid="service-rates">
      <h2 className="text-3xl font-bold text-center mb-2">{t('rates.title')}</h2>
      <p className="text-center text-muted-foreground mb-8">
        {t('rates.subtitle')}
      </p>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedGroupedRates.map(({ serviceType, rates: sortedRates, image_url }) => (
          <Card key={serviceType} className="hover:shadow-lg transition-shadow" data-testid={`service-card-${serviceType.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardHeader>
              {image_url && (
                <div className="aspect-video w-full overflow-hidden rounded-md mb-4">
                  <img
                    src={image_url}
                    alt={serviceType}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardTitle className="text-xl" data-testid={`service-title-${serviceType.toLowerCase().replace(/\s+/g, '-')}`}>{serviceType}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sortedRates.map((rate) => (
                    <div 
                      key={rate.id} 
                      className="flex justify-between items-center p-3 rounded-lg bg-muted/50"
                      data-testid={`rate-${rate.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{rate.duration_minutes} {t('common.minutes')}</Badge>
                      </div>
                      <span className="text-lg font-semibold" data-testid={`price-${rate.id}`}>฿{rate.price_thb}</span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="mt-8 p-4 bg-muted/30 rounded-lg text-center" data-testid="travel-note">
        <p className="text-sm text-muted-foreground">
          {t('rates.travelNote')}
        </p>
        <div className="mt-4 flex justify-center">
          <img 
            src={`${API}/uploads/taxi-rates.png`}
            alt="Taxi surcharge rates"
            className="rounded-lg shadow-md max-w-md w-full object-contain"
            data-testid="taxi-rates-image"
          />
        </div>
      </div>
    </section>
  );
};

export default ServiceRates;